/**
 * useClientOptimization – client-side grid search / walk-forward hook.
 *
 * Fetches candles (and optionally funding rates) from the REST API,
 * then delegates the full optimization computation to a Web Worker.
 * This hook is a pure computation hook — it does NOT depend on chatStore
 * or backtestStore. The caller wires up the results as needed.
 */

import { useState, useCallback, useRef } from 'react';
import { getCandles, getFundingRates, submitClientGridSearch, submitClientWalkForward } from '../api/client.js';
import type { GridSearchBacktestConfig } from '../api/client.js';
import type {
  WorkerMessage,
  GridSearchResult,
  WalkForwardResult,
  ParamDef,
  ParamRange,
} from '../workers/backtest-worker-types.js';
import type { Timeframe } from '../types.js';
import { WARMUP_BARS, resolveSlippagePercent } from '../../core/constants.js';
import { timeframeToMs } from '../../core/types.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ClientOptimizationInput {
  strategyCode: string;
  strategyName: string;
  symbol: string;
  timeframe: string;
  paramDefs: ParamDef[];
  paramRanges?: Record<string, ParamRange>;
  startDate: string;  // ISO date string, e.g. "2024-01-01"
  endDate: string;    // ISO date string, e.g. "2024-06-01"
  initialCapital: number;
  exchange?: string;
  leverage?: number;
  takerFeeRate?: number;
  slippagePercent?: number;
  optimizeFor?: 'sharpeRatio' | 'totalReturnPercent' | 'profitFactor' | 'winRate' | 'composite';
  maxCombinations?: number;
  minTrades?: number;
  // Walk-forward specific
  walkForward?: boolean;       // if true, run walk-forward instead of just grid search
  walkForwardWindows?: number; // default 5
  trainRatio?: number;         // default 0.7
  /**
   * Migration 065: who owns this optimization run.
   * Pass 'strategy' + the user_strategies.id for single-strategy GS/WF.
   * Pass 'aggregation' + the aggregation_configs.id for portfolio GS/WF.
   * When provided, the persisted row is linked back to the owner so the
   * owner detail page can surface this run in its Optimizations section.
   */
  ownerType?: 'strategy' | 'aggregation';
  ownerId?: string;
}

export interface OptimizationProgress {
  phase: 'grid_search' | 'walk_forward';
  // Grid search progress
  current: number;
  total: number;
  percent: number;
  bestSoFar?: { params: Record<string, unknown>; metric: number; metricName: string };
  // Walk-forward progress
  windowIndex?: number;
  totalWindows?: number;
  wfPhase?: 'optimizing' | 'testing';
}

export type OptimizationResult =
  | { type: 'grid_search'; result: GridSearchResult }
  | { type: 'walk_forward'; result: WalkForwardResult };

/** Options for the useClientOptimization hook. */
export interface ClientOptimizationOptions {
  /**
   * Worker execution timeout in milliseconds.
   * Defaults to 300 000 ms (5 minutes) since optimization runs many backtests.
   */
  timeoutMs?: number;
}

interface ClientOptimizationState {
  running: boolean;
  progress: OptimizationProgress | null;
  error: string | null;
  result: OptimizationResult | null;
}

const DEFAULT_TIMEOUT_MS = 300_000;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useClientOptimization(options: ClientOptimizationOptions = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const [state, setState] = useState<ClientOptimizationState>({
    running: false,
    progress: null,
    error: null,
    result: null,
  });

  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Clear the running timeout if one is active. */
  const clearWorkerTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (input: ClientOptimizationInput): Promise<OptimizationResult> => {
      // Terminate any existing worker before starting a new one
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      clearWorkerTimeout();

      setState({ running: true, progress: null, error: null, result: null });

      const exchange = input.exchange ?? 'bybit';

      // ── 1. Fetch candles ──────────────────────────────────────────────────
      let fetchedCandles: import('../types.js').Candle[] = [];
      let fundingRates:
        | Array<{ timestamp: number; fundingRate: number; markPrice?: number }>
        | undefined;

      try {
        // Extend the fetch start by WARMUP_BARS candles so indicators are primed
        // before trading begins at config.startDate. The worker only trades bars
        // >= config.startDate, so the warmup candles prime indicators only.
        const startDateMs = new Date(input.startDate).getTime();
        const warmupFetchStart = new Date(
          startDateMs - WARMUP_BARS * timeframeToMs(input.timeframe as Timeframe),
        ).toISOString();

        fetchedCandles = await getCandles({
          exchange,
          symbol: input.symbol,
          timeframe: input.timeframe as Timeframe,
          startDate: warmupFetchStart,
          endDate: input.endDate,
        });

        if (!fetchedCandles.length) {
          throw new Error('No candles found for the selected date range');
        }

        // ── 2. Fetch funding rates ────────────────────────────────────────
        const frResponse = await getFundingRates({
          exchange,
          symbol: input.symbol,
          start: new Date(input.startDate).getTime(),
          end: new Date(input.endDate).getTime(),
        });
        fundingRates = frResponse.rates;
      } catch (err) {
        clearWorkerTimeout();
        const errorMsg = err instanceof Error ? err.message : String(err);
        setState({ running: false, progress: null, error: errorMsg, result: null });
        throw err;
      }

      // ── 3. Wrap worker lifecycle in a Promise ─────────────────────────────
      return new Promise<OptimizationResult>((resolve, reject) => {
        // Guard against race conditions: only the first terminal event (success,
        // error, or timeout) processes. Subsequent events are ignored.
        let settled = false;

        const worker = new Worker(
          new URL('../workers/backtest.worker.ts', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;

        // ── 4. Start timeout watchdog ───────────────────────────────────
        timeoutRef.current = setTimeout(() => {
          if (settled) return;
          settled = true;
          const errorMsg = `Optimization timed out after ${Math.round(timeoutMs / 1000)}s`;
          worker.terminate();
          workerRef.current = null;
          timeoutRef.current = null;
          setState({ running: false, progress: null, error: errorMsg, result: null });
          reject(new Error(errorMsg));
        }, timeoutMs);

        worker.onmessage = (e: MessageEvent<WorkerMessage>) => {
          const msg = e.data;

          switch (msg.type) {
            case 'grid_search_progress': {
              setState((prev) => ({
                ...prev,
                progress: {
                  phase: 'grid_search',
                  current: msg.current,
                  total: msg.total,
                  percent: msg.percent,
                  bestSoFar: msg.bestSoFar,
                },
              }));
              break;
            }

            case 'walk_forward_progress': {
              setState((prev) => ({
                ...prev,
                progress: {
                  phase: 'walk_forward',
                  current: msg.gridProgress?.current ?? 0,
                  total: msg.gridProgress?.total ?? 0,
                  percent: msg.gridProgress?.percent ?? 0,
                  windowIndex: msg.windowIndex,
                  totalWindows: msg.totalWindows,
                  wfPhase: msg.phase,
                },
              }));
              break;
            }

            case 'grid_search_result': {
              if (settled) break;
              settled = true;
              clearWorkerTimeout();
              worker.terminate();
              workerRef.current = null;

              if (msg.success) {
                const optimizationResult: OptimizationResult = {
                  type: 'grid_search',
                  result: msg.result,
                };
                // Persist to database (fire-and-forget — UI already has the result)
                submitClientGridSearch({
                  strategyName: input.strategyName,
                  symbol: input.symbol,
                  timeframe: input.timeframe,
                  startDate: new Date(input.startDate).getTime(),
                  endDate: new Date(input.endDate).getTime(),
                  initialCapital: input.initialCapital,
                  exchange: backtestConfigForPersistence.exchange,
                  bestParams: msg.result.bestParams,
                  bestMetrics: msg.result.bestMetrics as Record<string, unknown>,
                  totalCombinations: msg.result.totalCombinations,
                  testedCombinations: msg.result.testedCombinations,
                  topResults: msg.result.topResults.map((r) => ({
                    params: r.params,
                    metrics: r.metrics as Record<string, unknown>,
                  })),
                  backtestConfig: backtestConfigForPersistence,
                  // Migration 065: link the result to its owner so the owner detail
                  // page can surface this run in its Optimizations section.
                  ...(input.ownerType != null ? { ownerType: input.ownerType } : {}),
                  ...(input.ownerId != null ? { ownerId: input.ownerId } : {}),
                }).catch(() => {
                  // Silently ignore save failures — result is already displayed
                });
                setState({
                  running: false,
                  progress: null,
                  error: null,
                  result: optimizationResult,
                });
                resolve(optimizationResult);
              } else {
                setState({
                  running: false,
                  progress: null,
                  error: msg.error,
                  result: null,
                });
                reject(new Error(msg.error));
              }
              break;
            }

            case 'walk_forward_result': {
              if (settled) break;
              settled = true;
              clearWorkerTimeout();
              worker.terminate();
              workerRef.current = null;

              if (msg.success) {
                const optimizationResult: OptimizationResult = {
                  type: 'walk_forward',
                  result: msg.result,
                };
                // Persist to database (fire-and-forget — UI already has the result)
                submitClientWalkForward({
                  strategyName: input.strategyName,
                  symbol: input.symbol,
                  timeframe: input.timeframe,
                  startDate: new Date(input.startDate).getTime(),
                  endDate: new Date(input.endDate).getTime(),
                  initialCapital: input.initialCapital,
                  exchange: input.exchange ?? 'bybit',
                  bestParams: msg.result.bestParams,
                  passed: msg.result.passed,
                  averageTrainSharpe: msg.result.averageTrainSharpe,
                  averageTestSharpe: msg.result.averageTestSharpe,
                  sharpeDegradation: msg.result.sharpeDegradation,
                  windows: msg.result.windows.map((w) => ({
                    windowIndex: w.windowIndex,
                    trainStart: w.trainStart,
                    trainEnd: w.trainEnd,
                    testStart: w.testStart,
                    testEnd: w.testEnd,
                    bestTrainParams: w.bestTrainParams,
                    trainMetrics: w.trainMetrics as Record<string, unknown>,
                    testMetrics: w.testMetrics as Record<string, unknown>,
                  })),
                  // Migration 065: link the result to its owner so the owner detail
                  // page can surface this run in its Optimizations section.
                  ...(input.ownerType != null ? { ownerType: input.ownerType } : {}),
                  ...(input.ownerId != null ? { ownerId: input.ownerId } : {}),
                }).catch(() => {
                  // Silently ignore save failures — result is already displayed
                });
                setState({
                  running: false,
                  progress: null,
                  error: null,
                  result: optimizationResult,
                });
                resolve(optimizationResult);
              } else {
                setState({
                  running: false,
                  progress: null,
                  error: msg.error,
                  result: null,
                });
                reject(new Error(msg.error));
              }
              break;
            }

            // Ignore single-backtest messages — those are handled by useClientBacktest
            default:
              break;
          }
        };

        worker.onerror = (e: ErrorEvent) => {
          if (settled) return;
          settled = true;
          clearWorkerTimeout();
          const errorMsg = e.message || 'Worker error';
          setState({ running: false, progress: null, error: errorMsg, result: null });
          worker.terminate();
          workerRef.current = null;
          reject(new Error(errorMsg));
        };

        // ── 5. Post input to worker ─────────────────────────────────────
        //
        // backtestConfigForPersistence captures the EXACT same values used
        // by the worker so the rerun page can reproduce identical metrics.
        // Keep in sync with baseConfig — they MUST mirror each other.
        const backtestConfigForPersistence: GridSearchBacktestConfig = {
          initialCapital: input.initialCapital,
          leverage: input.leverage ?? 1,
          takerFeeRate: input.takerFeeRate ?? 0.001,
          // SHA-28: resolve via the single shared resolveSlippagePercent helper
          // (mode is always 'futures' here, so this is behavior-preserving —
          // resolveSlippagePercent('futures', undefined) === 0.05, same as the
          // previous hardcoded literal) rather than duplicating the default.
          slippagePercent: resolveSlippagePercent('futures', input.slippagePercent),
          mode: 'futures',
          exchange: input.exchange ?? 'bybit',
        };

        const baseConfig = {
          symbol: input.symbol,
          timeframe: input.timeframe,
          initialCapital: backtestConfigForPersistence.initialCapital,
          leverage: backtestConfigForPersistence.leverage,
          takerFeeRate: backtestConfigForPersistence.takerFeeRate,
          slippagePercent: backtestConfigForPersistence.slippagePercent,
          mode: backtestConfigForPersistence.mode,
          startDate: new Date(input.startDate).getTime(),
          endDate: new Date(input.endDate).getTime(),
        };

        if (input.walkForward) {
          worker.postMessage({
            type: 'walk_forward',
            strategyCode: input.strategyCode,
            candles: fetchedCandles,
            fundingRates,
            paramDefs: input.paramDefs,
            paramRanges: input.paramRanges,
            maxCombinations: input.maxCombinations ?? 500,
            minTrades: input.minTrades ?? 10,
            optimizeFor: input.optimizeFor ?? 'sharpeRatio',
            windows: input.walkForwardWindows ?? 5,
            trainRatio: input.trainRatio ?? 0.7,
            config: baseConfig,
          });
        } else {
          worker.postMessage({
            type: 'grid_search',
            strategyCode: input.strategyCode,
            candles: fetchedCandles,
            fundingRates,
            paramDefs: input.paramDefs,
            paramRanges: input.paramRanges,
            maxCombinations: input.maxCombinations ?? 500,
            minTrades: input.minTrades ?? 10,
            optimizeFor: input.optimizeFor ?? 'sharpeRatio',
            config: baseConfig,
          });
        }
      });
    },
    [timeoutMs, clearWorkerTimeout],
  );

  const cancel = useCallback(() => {
    clearWorkerTimeout();
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setState({ running: false, progress: null, error: 'Cancelled', result: null });
  }, [clearWorkerTimeout]);

  return { ...state, run, cancel };
}
